import React, { FC, ForwardedRef, forwardRef } from 'react';
import { FormControl, MenuItem, Select, InputLabel, FormHelperText, OutlinedInput } from '@mui/material';
import { ILookupItem } from '../../../../types/genericTypes';
import { v4 as uuidv4 } from 'uuid';

interface SelectProps {
    label?: string,
    fieldName: string,
    value: number | string,
    listItems: string[] | number[] | ILookupItem[],
    variant?: 'filled' | 'outlined' | 'standard',
    onChange: (value: unknown, fieldName: string) => void,
    onBlur?: () => void,
    title?: string,
    popoverClasses?: string,
    error?: string,
    className?: string,
    disabled?: boolean,
    readOnly?: boolean,
    ref?: ForwardedRef<HTMLSelectElement | undefined>,
    color?: string,
    borderColor?: string,
    focusBorderColor?: string,
    menuColor?: string
}

// const defaultItem = <MenuItem value={0} disabled>Select One</MenuItem>

const CheckmateSelect: FC<SelectProps> = forwardRef(({label, listItems, value, fieldName, variant = 'outlined', onChange, onBlur, title = '', popoverClasses = '', className = '', error = '', disabled = false, readOnly = false, color, borderColor, focusBorderColor, menuColor}, ref) => {
    const randInt = uuidv4();
    return <FormControl
        className={`form-control ${className}`}
        variant={variant}
        size="small"
        error={!!error.length}
        sx={{
            borderBottom: 'none',
            '&:hover .MuiInputLabel-root': {
                color: focusBorderColor || 'default',
            },
            '&.Mui-focused .MuiInputLabel-root': {
                color: focusBorderColor || 'default',
            },
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d32f2f !important',
            },
            '& .MuiInputLabel-root.Mui-error': {
                color: '#d32f2f !important',
            },
            '& .MuiFormHelperText-root': {
                color: `${color || 'default'} !important`,
                '&.Mui-error': {
                    color: '#d32f2f !important'
                }
            }
        }}
    >
        {label && (
            <InputLabel
                id={`${randInt}-select-id`}
                sx={{
                    color: borderColor || 'default',
                    '&.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.38)',
                    },
                }}
            >
                {label}
            </InputLabel>
        )}
        <Select
            ref={ref}
            title={title}
            labelId={`${randInt}-select-id`}
            label={label}
            onChange={(e) => onChange(e.target.value, fieldName)}
            onBlur={onBlur && onBlur}
            value={value}
            readOnly={readOnly}
            disabled={disabled}
            input={<OutlinedInput label={label} error={!!error.length} />}
            sx={{
                backgroundColor: 'inherit',
                borderColor: borderColor || 'default',
                color: color || 'default',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: borderColor || 'default',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: focusBorderColor || 'default',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: focusBorderColor || 'default',
                },
                '& .MuiSelect-icon': {
                    color: color || 'default',
                },
                '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    color: 'rgba(0, 0, 0, 0.38)',
                    cursor: 'not-allowed',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.26)',
                    },
                    '& .MuiSelect-icon': {
                        color: 'rgba(0, 0, 0, 0.38)',
                    },
                },
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left"
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left"
              },
              PopoverClasses: {
                  'root': popoverClasses,
                  'paper': popoverClasses
              },
              PaperProps: {
                sx: {
                  backgroundColor: color || 'default',
                  color: menuColor || 'default'
                }
              }
            }}
        >
            {listItems?.length > 0 && listItems.map((item: string | number | ILookupItem, index: number) => {
                if ((item as ILookupItem)?.id) {
                    const tempItem = item as ILookupItem;
                    return <MenuItem key={tempItem.id} value={tempItem.id} sx={{
                        '&.Mui-selected': {
                            backgroundColor: focusBorderColor,
                            color: 'default',
                        },
                        '&.Mui-selected:hover': {
                            backgroundColor: `${focusBorderColor}66`,
                        },
                    }}>{tempItem.name}</MenuItem>
                }
                const tempItem = item as string | number;
                return <MenuItem key={index} value={tempItem} sx={{
                    '&.Mui-selected': {
                        backgroundColor: focusBorderColor,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: `${focusBorderColor}33`,
                    },
                }}>{tempItem}</MenuItem>
            })}
        </Select>
        {error?.length > 0 && <FormHelperText error={true}>{error}</FormHelperText>}
    </FormControl>;
})

export default CheckmateSelect;